import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const PharmDCoursePage: React.FC = () => {
  const courseData = {
    title: "Pharm.D (Doctor of Pharmacy)",
    shortName: "Pharm.D",
    duration: "6 years",
    description: "Professional doctorate program combining pharmaceutical sciences with clinical pharmacy practice",
    overview: "Pharm.D (Doctor of Pharmacy) is a 6-year professional doctorate program that combines rigorous pharmaceutical sciences education with clinical pharmacy practice. The program prepares students to become clinical pharmacy experts capable of managing drug therapy, counseling patients, and contributing to healthcare outcomes. It emphasizes evidence-based patient care, medication management, and clinical decision-making.",
    eligibility: [
      "10+2 with Physics, Chemistry, and Biology",
      "Minimum 50% marks in PCB subjects",
      "Valid NEET score (for most universities)",
      "Age between 17-25 years",
      "Good written and communication skills"
    ],
    
    curriculum: [
      {
        year: 1,
        subjects: [
          "Pharmaceutical Chemistry I",
          "Pharmacognosy",
          "Biochemistry",
          "Human Anatomy and Physiology",
          "Pharmaceutical Analysis",
          "Pharmacy Practice I",
          "Communication Skills",
          "Professional Ethics"
        ]
      },
      {
        year: 2,
        subjects: [
          "Pharmaceutical Chemistry II",
          "Medicinal Chemistry",
          "Pharmacology",
          "Pharmaceutics I",
          "Pharmaceutical Microbiology",
          "Pharmacy Practice II",
          "Pathophysiology",
          "Clinical Biochemistry"
        ]
      },
      {
        year: 3,
        subjects: [
          "Biopharmaceutics and Pharmacokinetics",
          "Pharmaceutics II",
          "Pharmacotherapeutics I",
          "Industrial Pharmacy",
          "Pharmaceutical Regulatory Affairs",
          "Biostatistics",
          "Clinical Pharmacy",
          "Hospital Pharmacy Practice"
        ]
      },
      {
        year: 4,
        subjects: [
          "Pharmacotherapeutics II",
          "Clinical Toxicology",
          "Pharmaceutical Quality Assurance",
          "Clinical Drug Interactions",
          "Geriatric Pharmacy",
          "Pediatric Pharmacy",
          "Oncology Pharmacy",
          "Advanced Clinical Practice"
        ]
      },
      {
        year: 5,
        subjects: [
          "Advanced Pharmacotherapeutics",
          "Patient Counseling & Communication",
          "Disease State Management",
          "Primary Healthcare",
          "Pharmaceutical Marketing",
          "Medical Affairs",
          "Clinical Rotation I",
          "Experiential Learning"
        ]
      },
      {
        year: 6,
        subjects: [
          "Clinical Rotation II",
          "Research Project/Dissertation",
          "Clinical Internship",
          "Case Studies & Presentations",
          "Specialized Clinical Training",
          "Patient Management",
          "Healthcare Leadership",
          "Professional Practice"
        ]
      }
    ],

    careerProspects: {
      roles: [
        "Clinical Pharmacist",
        "Medication Therapy Manager",
        "Hospital Pharmacist",
        "Community Pharmacist",
        "Pharmaceutical Researcher",
        "Drug Information Specialist",
        "Regulatory Affairs Manager",
        "Medical Affairs Executive",
        "Pharmacy Educator",
        "Pharmaceutical Consultant"
      ],
      industries: [
        "Hospitals and Healthcare Centers",
        "Retail Pharmacies",
        "Pharmaceutical Companies",
        "Regulatory Agencies",
        "Clinical Research Organizations",
        "Medical Device Companies",
        "Government Health Services",
        "Educational Institutions",
        "Healthcare Analytics",
        "International Pharmaceutical Organizations"
      ],
      averageSalary: "₹5-8 LPA (Fresher), ₹20-50 LPA (Experienced/Senior Positions)",
      topRecruiters: [
        "Cipla",
        "Dr. Reddy's Laboratories",
        "Lupin",
        "Sun Pharmaceutical",
        "Apollo Hospitals",
        "Max Healthcare",
        "Fortis Hospitals",
        "GlaxoSmithKline",
        "Novartis",
        "Roche"
      ]
    },

    admissionProcess: [
      "NEET qualification and valid score",
      "Merit-based selection from candidates",
      "University entrance examination (optional for some)",
      "Interview and counseling process",
      "Merit list publication",
      "Document verification",
      "Medical fitness certificate",
      "Final admission confirmation and registration"
    ],

    topColleges: [
      {
        name: "JSS College of Pharmacy",
        location: "Mysore, Karnataka",
        fees: "₹2-2.5 LPA",
        ranking: "NIRF Ranking: 10 (Pharmacy)"
      },
      {
        name: "Manipal College of Pharmaceutical Sciences",
        location: "Manipal, Karnataka",
        fees: "₹2.5-3 LPA",
        ranking: "NIRF Ranking: 12 (Pharmacy)"
      },
      {
        name: "Delhi Institute of Pharmaceutical Sciences and Research (DIPSAR)",
        location: "Delhi",
        fees: "₹50,000-1 LPA",
        ranking: "Government Institute"
      },
      {
        name: "Jamia Hamdard School of Pharmaceutical Education and Research",
        location: "Delhi",
        fees: "₹1-1.5 LPA",
        ranking: "AICTE Approved"
      }
    ],

    skills: [
      "Clinical Patient Assessment",
      "Medication Therapy Management",
      "Drug Interaction Analysis",
      "Pharmacotherapy Knowledge",
      "Patient Counseling",
      "Healthcare Communication",
      "Critical Thinking",
      "Research and Data Analysis",
      "Healthcare Quality Management",
      "Team Leadership",
      "Problem Solving",
      "Professional Ethics and Compliance"
    ],

    futureScope: "Pharm.D graduates have exceptional career opportunities in clinical settings, pharmaceutical industry, and international healthcare. Growing emphasis on clinical pharmacy in hospitals, expanding role of pharmacists in healthcare teams, opportunities in specialized clinical areas, international practice options, and potential for research and academic careers.",

    faqs: [
      {
        question: "What is the difference between Pharm.D and M.Pharm?",
        answer: "Pharm.D is a 6-year professional doctorate with emphasis on clinical pharmacy and patient care. M.Pharm is a 2-year master's program focusing on research and specialization in pharmaceutical sciences. Pharm.D is more clinically oriented while M.Pharm is more research-oriented."
      },
      {
        question: "Can I do Pharm.D after completing B.Pharm?",
        answer: "In most Indian universities, Pharm.D is an entry-level program done after 12th grade. Some universities may offer bridge programs for B.Pharm graduates. It's best to check with specific universities."
      },
      {
        question: "Is Pharm.D recognized internationally?",
        answer: "Yes, Pharm.D is widely recognized internationally. However, to practice as a pharmacist in countries like USA, Canada, or Australia, you may need to pass additional licensing exams or complete post-doctoral training."
      },
      {
        question: "What is the scope of clinical pharmacy in India?",
        answer: "Clinical pharmacy is rapidly growing in India with increasing hospital employment opportunities. Many multispecialty hospitals are hiring clinical pharmacists for drug information services, medication therapy management, and patient counseling."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {courseData.title}
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={courseData} />
      </div>
    </div>
  );
};

export default PharmDCoursePage;
